import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, PhoneMissed, Clock, CalendarX, TrendingDown } from 'lucide-react';
import { Button } from '../ui/button';

const painPoints = [
  {
    icon: PhoneMissed,
    title: "Calls & DMs Go Unanswered",
    description: "You're busy, you're asleep, you're in traffic — and the customer simply moves on. Small businesses miss 20–35% of calls during work hours and nearly all of them after hours.",
    stat: "85%",
    statLabel: "of callers who hit voicemail hang up without leaving a message"
  },
  {
    icon: Clock,
    title: "Slow Replies Lose Sales",
    description: "Nigerian customers message three businesses at once and book whoever replies first. If your answer takes hours, you were never in the race.",
    stat: "21x",
    statLabel: "more likely to win an enquiry when you reply within 5 minutes"
  },
  {
    icon: CalendarX,
    title: "No-Shows Drain Your Revenue",
    description: "Clinics, salons, consultants and shortlet hosts lose 15–30% of bookings to forgotten appointments — and re-selling that empty slot is almost impossible.",
    stat: "38%",
    statLabel: "average reduction in no-shows with automated reminders"
  },
  {
    icon: TrendingDown,
    title: "Leads Die Without Follow-Up",
    description: "'I'll get back to you' becomes silence. 80% of sales need five or more follow-ups — but most businesses give up after just one.",
    stat: "80%",
    statLabel: "of sales require 5+ follow-ups — yet 44% stop after one"
  }
];

const PainPoints = () => {
  return (
    <section className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-red-100 text-red-600 text-sm font-medium mb-6">
            The Problem
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Is This <span className="text-red-500">Killing</span> Your Business?
          </h2>
          <p className="text-xl text-gray-600">
            If any of these sound familiar, you're leaving serious money on the table — every single day.
          </p>
        </div>

        {/* Pain Points Grid */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 mb-16">
          {painPoints.map((pain, index) => (
            <div
              key={index}
              className="group relative bg-white rounded-2xl p-8 border border-gray-200 hover:border-red-200 transition-all duration-300 hover:shadow-xl hover:shadow-red-500/5"
            >
              {/* Icon */}
              <div className="w-14 h-14 rounded-xl bg-red-50 flex items-center justify-center mb-6 group-hover:bg-red-100 transition-colors">
                <pain.icon className="w-7 h-7 text-red-500" />
              </div>

              {/* Content */}
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {pain.title}
              </h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                {pain.description}
              </p>

              {/* Stat */}
              <div className="flex items-end gap-3">
                <span className="text-4xl font-bold text-red-500">
                  {pain.stat}
                </span>
                <span className="text-sm text-gray-500 pb-1">
                  {pain.statLabel}
                </span>
              </div>

              {/* Hover indicator */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-red-400 to-red-600 rounded-b-2xl scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="text-center">
          {/* Block, not inline-block: shrink-to-fit was resolving 25px wider
              than the column on a 375px screen, which pushed the document's
              scroll width past the viewport and let the whole page pan
              sideways. A centred block cannot exceed its column. */}
          <div className="mx-auto bg-gradient-to-r from-red-50 to-amber-50 rounded-2xl p-6 sm:p-12 max-w-3xl">
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              The Good News? <span className="text-emerald-500">We Fix This.</span>
            </h3>
            <p className="text-gray-600 mb-8 text-lg">
              In 7–14 days, every call and message could be answered instantly — and every
              lead followed up automatically — while you focus on running your business.
            </p>
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-8 py-6 text-lg rounded-xl shadow-lg shadow-emerald-500/25 transition-all hover:shadow-emerald-500/40 hover:scale-105"
              >
                Let's Fix Your Follow-Up
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PainPoints;
